import { type Extension, RangeSetBuilder, StateEffect, StateField } from '@codemirror/state';
import { Decoration, EditorView, WidgetType, type DecorationSet } from '@codemirror/view';
import { type Change, diffLines } from 'diff';

export interface DiffResult {
  originalContent: string;
  proposedContent: string;
  changes: Change[];
  hasChanges: boolean;
}

export function computeDiff(original: string, proposed: string): DiffResult {
  const changes = diffLines(original, proposed);
  const hasChanges = changes.some((c) => c.added || c.removed);
  return { originalContent: original, proposedContent: proposed, changes, hasChanges };
}

class AddedLineWidget extends WidgetType {
  constructor(readonly text: string) {
    super();
  }

  toDOM(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'cm-diff-added';
    wrap.style.background = 'rgba(166, 227, 161, 0.15)';
    wrap.style.borderLeft = '3px solid #a6e3a1';
    wrap.style.padding = '0 4px';
    wrap.style.fontFamily = 'ui-monospace, monospace';
    wrap.style.fontSize = '13px';
    wrap.style.whiteSpace = 'pre';
    wrap.style.color = '#a6e3a1';
    wrap.textContent = this.text;
    return wrap;
  }

  ignoreEvent(): boolean {
    return true;
  }
}

const removedLineDeco = Decoration.line({
  attributes: {
    style:
      'background: rgba(243, 139, 168, 0.15); text-decoration: line-through; text-decoration-color: #f38ba8; opacity: 0.7; color: #f38ba8;',
  },
});

export const setDiffEffect = StateEffect.define<DiffResult | null>();

const diffField = StateField.define<DecorationSet>({
  create() {
    return Decoration.none;
  },
  update(decos, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setDiffEffect)) {
        if (!effect.value) return Decoration.none;
        return buildDecorations(tr.state.doc.toString(), effect.value);
      }
    }
    return decos;
  },
  provide: (f) => EditorView.decorations.from(f),
});

function buildDecorations(docText: string, diff: DiffResult): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>();
  const lines = docText.split('\n');
  let docLine = 0;

  for (const change of diff.changes) {
    const changeLines = (
      change.value.endsWith('\n') ? change.value.slice(0, -1) : change.value
    ).split('\n');

    if (change.removed) {
      for (const _ of changeLines) {
        if (docLine < lines.length) {
          const lineObj = getLineAt(docText, docLine);
          if (lineObj) {
            builder.add(lineObj.from, lineObj.from, removedLineDeco);
          }
          docLine++;
        }
      }
    } else if (change.added) {
      const insertPos = docLine < lines.length ? getLineAt(docText, docLine) : null;
      const pos = insertPos ? insertPos.from : docText.length;
      for (const addedLine of changeLines) {
        builder.add(
          pos,
          pos,
          Decoration.widget({ widget: new AddedLineWidget(addedLine), side: -1 }),
        );
      }
    } else {
      docLine += changeLines.length;
    }
  }

  return builder.finish();
}

function getLineAt(doc: string, lineIndex: number): { from: number; to: number } | null {
  const lines = doc.split('\n');
  if (lineIndex >= lines.length) return null;
  let from = 0;
  for (let i = 0; i < lineIndex; i++) {
    from += lines[i].length + 1;
  }
  return { from, to: from + lines[lineIndex].length };
}

export function diffExtension(): Extension {
  return [diffField];
}
